import { useMemo, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "@/api/errors";
import type { components } from "@/api/schema";
import { useCan } from "@/shared/auth";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import { PageHeader } from "@/shared/components/PageHeader";
import { Pagination } from "@/shared/components/Pagination";
import { toast } from "@/shared/lib/toast";

import { useArchiveCampaign } from "../api/use-archive-campaign";
import { useCampaigns } from "../api/use-campaigns";
import { useDeleteCampaign } from "../api/use-delete-campaign";
import { CampaignFormModal } from "../components/CampaignFormModal";
import { CampaignsToolbar, type StatusFilter } from "../components/CampaignsToolbar";
import { CampaignTable } from "../components/CampaignTable";
import { campaignsToCsv } from "../lib/campaigns-csv";
import { downloadCsv } from "../lib/report-csv";

type Campaign = components["schemas"]["Campaign"];

/** Month-Year bucket for the (client-side) period filter, e.g. "May 2026". */
function periodBucket(c: Campaign): string | null {
  const iso = c.period_start ?? c.period_end;
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const month = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  return `${month} ${d.getUTCFullYear()}`;
}

interface CampaignsPageProps {
  /** Row/name click — routes wire this to `/promotions?campaign_id=` in F4. */
  onOpenCampaign?: (campaign: Campaign) => void;
}

export function CampaignsPage({ onOpenCampaign }: CampaignsPageProps): JSX.Element {
  const { t } = useTranslation(["campaign", "nav"]);
  const canManage = useCan("promotions:manage");

  // Search + rollup status filter are BE-supported query params (server-side).
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("");
  // Period is filtered client-side over the loaded rows (no BE bucket param).
  const [period, setPeriod] = useState("");
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);

  const { data, isLoading, isError } = useCampaigns({
    ...(search ? { search } : {}),
    ...(status ? { status_rollup: status } : {}),
  });
  const archive = useArchiveCampaign();
  const del = useDeleteCampaign();

  const [create, setCreate] = useState(false);
  const [rename, setRename] = useState<Campaign | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Campaign | null>(null);

  const loaded = useMemo(() => data?.items ?? [], [data]);

  // Period options derived from the currently-loaded rows.
  const periodOptions = useMemo(() => {
    const buckets = new Set<string>();
    for (const c of loaded) {
      const b = periodBucket(c);
      if (b) buckets.add(b);
    }
    return [...buckets];
  }, [loaded]);

  // Client-side period filter over the (server-filtered) rows.
  const items = useMemo(
    () => (period ? loaded.filter((c) => periodBucket(c) === period) : loaded),
    [loaded, period],
  );
  // /v1/campaigns has no offset/limit, so the page is sliced client-side (same as the report).
  const pageItems = useMemo(() => items.slice(offset, offset + limit), [items, offset, limit]);
  // Any filter change lands back on page 1.
  const filterKey = `${search}|${status}|${period}`;
  const [seenFilterKey, setSeenFilterKey] = useState(filterKey);
  if (filterKey !== seenFilterKey) {
    setSeenFilterKey(filterKey);
    setOffset(0);
  }

  function handleExport(): void {
    downloadCsv("campaigns-export.csv", campaignsToCsv(items));
  }

  function handleArchive(c: Campaign): void {
    archive.mutate(c.id, {
      onError: (err) => toast.error(apiErrorMessage(err)),
    });
  }

  function confirmDelete(): void {
    if (!deleteTarget) return;
    del.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
      onError: (err) => {
        // Active children → 409; surface the localized blocked message.
        if (
          err &&
          typeof err === "object" &&
          "status" in err &&
          (err as { status: number }).status === 409
        ) {
          toast.error(t("delete.blocked"));
        } else {
          toast.error(apiErrorMessage(err));
        }
      },
    });
  }

  return (
    <div data-testid="campaigns-page">
      <PageHeader
        breadcrumb={
          <>
            <span>{t("nav:group.promotion")}</span>
            <span className="text-[var(--premium-border-strong)]">/</span>
            <span className="text-[var(--text-secondary)]">{t("campaign:page.title")}</span>
          </>
        }
        title={t("page.title")}
      />

      <CampaignsToolbar
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={setStatus}
        period={period}
        onPeriodChange={setPeriod}
        periodOptions={periodOptions}
        onExport={handleExport}
        {...(canManage ? { onNewCampaign: () => setCreate(true) } : {})}
      />

      <CampaignTable
        items={pageItems}
        isLoading={isLoading}
        isError={isError}
        canManage={canManage}
        onRowClick={(c) => onOpenCampaign?.(c)}
        onRename={setRename}
        onArchive={handleArchive}
        onDelete={setDeleteTarget}
      />

      {!isLoading && !isError && (
        <Pagination
          total={items.length}
          offset={offset}
          limit={limit}
          pageSizeOptions={[10, 25, 50, 100]}
          onChange={({ offset: nextOffset, limit: nextLimit }) => {
            setOffset(nextOffset);
            setLimit(nextLimit);
          }}
        />
      )}

      {create && <CampaignFormModal onClose={() => setCreate(false)} />}
      {rename && (
        <CampaignFormModal
          campaign={{ id: rename.id, name: rename.name }}
          onClose={() => setRename(null)}
        />
      )}
      {deleteTarget && (
        <ConfirmDialog
          title={t("delete.confirm_title")}
          description={t("delete.confirm_body")}
          confirmLabel={t("action.delete")}
          cancelLabel={t("form.cancel")}
          isPending={del.isPending}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
